import { cn } from "@/lib/utils";
import { MessageCircle } from "lucide-react";
import { useEffect, useState } from "react";

interface WhatsAppButtonProps {
    message?: string;
    pulseAnimation?: boolean;
}

interface WhatsAppConfig {
    activo: boolean;
    numero: string;
    mensaje_inicial: string;
}

export default function WhatsAppButton({
    message,
    pulseAnimation = true,
}: WhatsAppButtonProps) {
    const [isVisible, setIsVisible] = useState(false);
    const [isHovered, setIsHovered] = useState(false);
    const [config, setConfig] = useState<WhatsAppConfig>({
        activo: false,
        numero: '',
        mensaje_inicial: ''
    });

    // Fetch WhatsApp configuration from API
    useEffect(() => {
        const fetchConfig = async () => {
            try {
                const response = await fetch(`${window.origin}/api/whatsapp/config`);
                if (response.ok) {
                    const data = await response.json();
                    console.log('WhatsApp Config:', data); // Debug
                    setConfig({
                        ...data,
                        activo: Boolean(data.activo) // Asegurar conversión a boolean
                    });
                }
            } catch (error) {
                console.error('Error fetching WhatsApp config:', error);
            }
        };

        fetchConfig();
    }, []);

    // Show button after a small delay for better UX
    useEffect(() => {
        if (config.activo) {
            const timer = setTimeout(() => {
                setIsVisible(true);
            }, 1000);

            return () => clearTimeout(timer);
        } else {
            setIsVisible(false);
        }
    }, [config.activo]);

    const handleWhatsAppClick = () => {
        const finalMessage = message || config.mensaje_inicial;
        const encodedMessage = encodeURIComponent(finalMessage);
        const phoneNumber = config.numero.replace(/\+/g, '');
        window.open(
            `https://wa.me/${phoneNumber}?text=${encodedMessage}`,
            "_blank"
        );
    };

    // Don't render if not active
    if (!config.activo) {
        return null;
    }

    return (
        <div
            className={cn(
                "fixed bottom-6 left-6 z-50 flex items-center transition-all duration-500 transform ",
                isVisible
                    ? "translate-y-0 opacity-100"
                    : "translate-y-16 opacity-0"
            )}
        >
            <button
                onClick={handleWhatsAppClick}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
                className="group relative flex items-center"
                aria-label="Chat on WhatsApp"
            >
                {/* Main button */}
                <div
                    className={cn(
                        "flex items-center justify-center rounded-full bg-green-500 text-white shadow-lg transition-all duration-300 bg-success",
                        "h-14 w-14 md:h-16 md:w-16",
                        isHovered ? "scale-110" : "",
                        pulseAnimation ? "animate-pulse-slow" : ""
                    )}
                >
                    <MessageCircle className="h-7 w-7 md:h-8 md:w-8" />

                    {/* Pulse effect */}
                    {pulseAnimation && (
                        <span className="absolute inset-0 rounded-full bg-green-500 opacity-60 animate-ping"></span>
                    )}
                </div>

                {/* Text label */}
                <div
                    className={cn(
                        "bg-white text-green-600 font-medium rounded-r-full py-2 px-4 shadow-md ml-2",
                        "transition-all duration-300 origin-left",
                        isHovered
                            ? "opacity-100 scale-100"
                            : "opacity-0 scale-90 pointer-events-none",
                        "whitespace-nowrap text-sm md:text-base"
                    )}
                >
                    ¿Necesitas ayuda?
                </div>
            </button>
        </div>
    );
}
